import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import XLSX from 'xlsx';
import { makeStyles, useTheme } from '@material-ui/styles';
import {
  Button,
  Checkbox,
  Dialog,
  Typography,
  colors,
  TextField,
  IconButton,
  Backdrop,
  useMediaQuery
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Editor, Loading } from 'components';

const schema = {
  dataFile: {
    presence: { allowEmpty: false, message: 'is required' }
  },
  name: {
    presence: { allowEmpty: false, message: 'is required' }
  },
  title: {
    presence: { allowEmpty: false, message: 'is required' }
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 600,
    maxWidth: 736,
    overflow: 'hidden',
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto'
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.grey[100],
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  checkbox: {
    paddingTop: 0,
    paddingBottom: 0
  },
  startButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  inputPadding: {
    padding: theme.spacing(0, 1)
  }
}));

const initForm = {
  isValid: false,
  values: { name: '', title: '', description: '', html: '', header: 'Y', dataFile: {}, rowCount: 0, colCount: 0 },
  touched: {},
  errors: {}
}

const DatasetModal = props => {
  const { open, onClose, onSubmit, className } = props;

  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);
  const [formState, setFormState] = useState(initForm);

  useEffect(() => {
    const errors = validate({ ...formState.values, dataFile: formState.values.dataFile.name }, schema);

    setFormState(formState => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {}
    }));
  }, [formState.values]);

  const handleChange = (name) => (event, delta, source, editor) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [name]:
          name === 'html'
            ? editor.getHTML()
            : event.target.type === 'checkbox'
              ? event.target.checked
              : event.target.value
      },
      touched: {
        ...formState.touched,
        [name]: true
      }
    }));
  };

  const handleClose = () => {
    onClose();
    setFormState(initForm);
  }

  const handleCheckbox = ({ target: { checked } }) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        rowCount: checked ? formState.values.rowCount - 1 : formState.values.rowCount + 1,
        header: checked ? 'Y' : 'N'
      }
    }));
  }

  const handleUpload = (name) => ({ target }) => {
    const { files: [file] } = target;
    const invalid = (file.size / 1024 / 1024) > 20;
    setInvalidSize(invalid);
    if (invalid) return;
    setOpenBackdrop(true);
    const reader = new FileReader();
    if (name === 'dataFile') {
      reader.onload = ({ target }) => {
        const isRgrFile = file.name.includes('.rgr');
        const [name] = file.name.split('.');
        if(isRgrFile) { 
          setFormState(formState => ({
            ...formState,
            values: {
              ...formState.values,
              name,
              rowCount: 0,
              colCount: 0,
              header: 'Y',
              dataFile: file
            }
          }));
        } else {
          const { SheetNames: [firstSheetName], Sheets } = XLSX.read(new Uint8Array(target.result), { type: 'array' });
          const firstSheet = Sheets[firstSheetName];
          let importData = XLSX.utils.sheet_to_csv(firstSheet);
          importData = importData.trim().split('\n');
          const rowCount = importData.length - 1;
          [importData] = importData;
          const colCount = importData.split(',').length;
          setFormState(formState => ({
            ...formState,
            values: {
              ...formState.values,
              name,
              rowCount,
              colCount,
              header: 'Y',
              dataFile: file
            }
          })); 
        }        
        setOpenBackdrop(false);
      }
      reader.readAsArrayBuffer(file);
    }
    if (name === 'docFile') {
      reader.onloadend = () => {
        setFormState(formState => ({
          ...formState,
          values: {
            ...formState.values,
            html: reader.result
          }
        }));
        setOpenBackdrop(false);
      }
      reader.readAsText(file)
    }
  };

  const hasError = field =>
    formState.touched[field] && formState.errors[field] ? true : false;
  const isFirstRow = formState.values.header === 'Y';

  return (
    <Dialog
      classes={{ paper: clsx(classes.root, className) }}
      fullScreen={fullScreen}
      maxWidth="md"
      onClose={onClose}
      open={open}
    >
      <Backdrop
        className={classes.backdrop}
        open={openBackdrop}
      >
        <Loading />
      </Backdrop>
      <div className={classes.header}>
        <Typography
          variant="h3"
        >
          Upload Dataset
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <form
        className={classes.main}
        id="main"
        onSubmit={e => {
          e.preventDefault();
          if (formState.isValid && !invalidSize) {
            onSubmit(formState.values, handleClose);
          }
        }}
      >
        <PerfectScrollbar className={classes.main}>
          <div className={classes.inputPadding}>
            <TextField
              InputProps={{
                endAdornment:
                  <Button
                    color="secondary"
                    component="label"
                    variant="contained"
                  >
                    Browse
                    <input
                      accept=".rgr, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      onChange={handleUpload('dataFile')}
                      style={{ display: 'none' }}
                      type="file"
                      value=""
                    />
                  </Button>
              }}
              disabled
              error={invalidSize}
              fullWidth
              helperText={
                invalidSize ? 'The maximum supported file sizes are: 20 MB.' : null
              }
              label="Data File"
              margin="normal"
              placeholder="Data File"
              required
              value={formState.values.dataFile.name || ''}
              variant="outlined"
            />
          </div>
          <div className={classes.inputPadding}>
            <TextField
              error={hasError('name')}
              fullWidth
              helperText={
                hasError('name') ? formState.errors.name[0] : null
              }
              label="Name"
              margin="dense"
              onChange={handleChange('name')}
              placeholder="Name"
              required
              value={formState.values.name}
              variant="outlined"
            />
          </div>
          <div className={classes.inputPadding}>
            <TextField
              error={hasError('title')}
              fullWidth
              helperText={
                hasError('title') ? formState.errors.title[0] : null
              }
              label="Title"
              margin="dense"
              onChange={handleChange('title')}
              placeholder="Title"
              required
              value={formState.values.title}
              variant="outlined"
            />
          </div>
          <div className={classes.inputPadding}>
            <TextField
              fullWidth
              label="Comment"
              margin="normal"
              onChange={handleChange('description')}
              placeholder="Comment"
              value={formState.values.description}
              variant="outlined"
            />
          </div>
          <Editor
            handleUpload={handleUpload('docFile')}
            onChange={handleChange('html')}
            placeholder="Enter documentation..."
            theme="snow"
            value={formState.values.html}
          />
        </PerfectScrollbar>
      </form>
      <div className={classes.actions}>
        <div className={classes.flexRow}>
          {Boolean(formState.values.dataFile.name) &&
            <React.Fragment>
              <Typography
                variant="caption"
              >
                Row Count {formState.values.rowCount} {isFirstRow && <>+ header</>} | Column Count {formState.values.colCount}
              </Typography>
              <Typography
                variant="caption"
              >
                <Checkbox
                  checked={isFirstRow}
                  className={classes.checkbox}
                  edge="start"
                  onClick={handleCheckbox}
                  size="small"
                />
                First row is variable names
              </Typography>
            </React.Fragment>
          }
        </div>
        <Button
          color="primary"
          disabled={!formState.isValid || invalidSize}
          endIcon={<PublishIcon />}
          form="main"
          type="submit"
          variant="contained"
        >
          Upload
        </Button>
      </div>
    </Dialog>
  );
};

DatasetModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool
};

export default DatasetModal;
